import { FormValidator } from '@app/calculators/effort-planner-form/form-validation/formValidator';
import { WithdrawalProductType } from '@generated/generatedEntities';
import { BillingContactDTO, BillingContactModel, BillingContactModelData } from './billingContactModel';
import { Validateable } from '@app/calculators/effort-planner-form/form-validation/validateable';
import { BehaviorSubject, Observable } from 'rxjs';
import { RegistrationModel } from './registrationModel';
import { AddressModel, AddressModelData, AddressModelDTO } from './addressModel';

export interface OrganisationDTO extends AddressModelDTO {
  doAutoSendPaymentEmail: boolean;
  name: string;
  numEmployees: number;
  siaIndividualMember: boolean;
  siaCorporateMember: boolean;
  withdrawalProductTypes: WithdrawalProductType[];
  billingContact: BillingContactDTO | undefined;
  country: string;
  phone: string;
}

export class OrganisationModelData {
  name: string | undefined;
  numEmployees: number | undefined;
  siaIndividualMember: boolean | undefined;
  siaCorporateMember: boolean | undefined;
  withdrawalProductTypes: WithdrawalProductType[] | undefined;
  billingContact: BillingContactModelData | undefined;
  country: string | undefined;
  phone: string | undefined;
}

export type OrganisationModelValidationField = 'name' | 'numEmployees' | 'country' | 'phone';

export class OrganisationModel extends AddressModel implements Validateable {
  private nameSubject$: BehaviorSubject<string | undefined>;
  private numEmployeesSubject$: BehaviorSubject<number | undefined>;
  private siaIndividualMemberSubject$: BehaviorSubject<boolean | undefined>;
  private siaCorporateMemberSubject$: BehaviorSubject<boolean | undefined>;
  private withdrawalProductTypesSubject$: BehaviorSubject<WithdrawalProductType[] | undefined>;
  private countrySubject$: BehaviorSubject<string | undefined>;
  private phoneSubject$: BehaviorSubject<string | undefined>;

  readonly name$: Observable<string | undefined>;
  readonly numEmployees$: Observable<number | undefined>;
  readonly siaIndividualMember$: Observable<boolean | undefined>;
  readonly siaCorporateMember$: Observable<boolean | undefined>;
  readonly withdrawalProductTypes$: Observable<WithdrawalProductType[] | undefined>;
  readonly billingContact: BillingContactModel;
  readonly country$: Observable<string | undefined>;
  readonly phone$: Observable<string | undefined>;

  readonly formValidator = new FormValidator<OrganisationModelValidationField>();

  constructor(
    data: OrganisationModelData | undefined,
    addressData: AddressModelData | undefined,
    private parentRegistrationModel?: RegistrationModel,
  ) {
    super(addressData);
    this.nameSubject$ = new BehaviorSubject<string | undefined>(data?.name);
    this.numEmployeesSubject$ = new BehaviorSubject<number | undefined>(data?.numEmployees);
    this.siaIndividualMemberSubject$ = new BehaviorSubject<boolean | undefined>(data?.siaIndividualMember);
    this.siaCorporateMemberSubject$ = new BehaviorSubject<boolean | undefined>(data?.siaCorporateMember);
    this.withdrawalProductTypesSubject$ = new BehaviorSubject<WithdrawalProductType[] | undefined>(
      data?.withdrawalProductTypes,
    );
    this.countrySubject$ = new BehaviorSubject<string | undefined>(data?.country);
    this.phoneSubject$ = new BehaviorSubject<string | undefined>(data?.phone);

    this.name$ = this.nameSubject$.asObservable();
    this.numEmployees$ = this.numEmployeesSubject$.asObservable();
    this.siaIndividualMember$ = this.siaIndividualMemberSubject$.asObservable();
    this.siaCorporateMember$ = this.siaCorporateMemberSubject$.asObservable();
    this.withdrawalProductTypes$ = this.withdrawalProductTypesSubject$.asObservable();
    this.billingContact = new BillingContactModel(data?.billingContact, undefined);
    this.country$ = this.countrySubject$.asObservable();
    this.phone$ = this.phoneSubject$.asObservable();
  }

  override isValid(): boolean {
    if (!this.billingAddressSameAsOrganisation) {
      return this.billingContact.isValid() && this.formValidator.isValid;
    }
    return super.isValid() && this.formValidator.isValid;
  }
  override resetValidations(): void {
    super.resetValidations();
    this.formValidator.reset();
    if (!this.billingAddressSameAsOrganisation) {
      this.billingContact.resetValidations();
    }
  }
  override validate(): void {
    super.validate();
    this.formValidator.validate('name', 'settings.messages.validate.orgname.required', (this.name?.length ?? 0) > 0);
    this.formValidator.validate('name', 'settings.messages.validate.orgname.minlength', (this.name?.length ?? 0) >= 1);
    this.formValidator.validate('name', 'settings.messages.validate.orgname.maxlength', (this.name?.length ?? 0) < 50);
    this.formValidator.validate(
      'numEmployees',
      'settings.messages.validate.orgNumEmployees.required',
      !Number.isNaN(this.numEmployees),
    );
    this.formValidator.validate(
      'numEmployees',
      'settings.messages.validate.orgNumEmployees.min',
      (this.numEmployees ?? 0) >= 1,
    );
    this.formValidator.validate(
      'country',
      'settings.messages.validate.orgCountry.required',
      (this.country?.length ?? 0) > 0,
    );
    const phone = this.phone?.replaceAll(/\s/g, '');
    this.formValidator.validate('phone', 'settings.messages.validate.orgPhone.required', (phone?.length ?? 0) > 0);
    this.formValidator.validate(
      'phone',
      'settings.messages.validate.orgPhone.pattern',
      /^(\+\d{9,20}|\d{10,20})$/.test(phone ?? ''),
    );
    this.formValidator.validate('phone', 'settings.messages.validate.orgPhone.minlength', (phone?.length ?? 0) >= 9);
    this.formValidator.validate('phone', 'settings.messages.validate.orgPhone.maxlength', (phone?.length ?? 0) <= 20);
    if (!this.billingAddressSameAsOrganisation) {
      this.billingContact.validate();
    }
  }
  get name() {
    return this.nameSubject$.value;
  }
  set name(value: string | undefined) {
    this.nameSubject$.next(value);
  }
  get numEmployees() {
    return this.numEmployeesSubject$.value;
  }
  set numEmployees(value: number | undefined) {
    this.numEmployeesSubject$.next(value);
  }
  get phone() {
    return this.phoneSubject$.value;
  }
  set phone(value: string | undefined) {
    this.phoneSubject$.next(value);
  }

  get siaIndividualMember() {
    return this.siaIndividualMemberSubject$.value ?? false;
  }
  set siaIndividualMember(value: boolean) {
    this.siaIndividualMemberSubject$.next(value);
  }
  get siaCorporateMember() {
    return this.siaCorporateMemberSubject$.value ?? false;
  }
  set siaCorporateMember(value: boolean) {
    this.siaCorporateMemberSubject$.next(value);
  }
  get withdrawalProductTypes() {
    return this.withdrawalProductTypesSubject$.value ?? [];
  }
  set withdrawalProductTypes(value: WithdrawalProductType[]) {
    this.withdrawalProductTypesSubject$.next(value);
  }
  get billingAddressSameAsOrganisation() {
    return this.parentRegistrationModel?.billingAddressSameAsOrganisation;
  }
  get country() {
    return this.countrySubject$.value ?? '';
  }
  set country(value: string) {
    this.countrySubject$.next(value);
  }

  toDto(): OrganisationDTO {
    // TODO: We should not allow default values to be set here.
    return {
      billingContact: this.billingAddressSameAsOrganisation ? undefined : this.billingContact.toDto(),
      name: this.name ?? '',
      numEmployees: this.numEmployees ?? 1,
      phone: this.phone ?? '',
      place: this.place ?? '',
      street: this.street ?? '',
      zipCode: this.zipCode ?? '',
      doAutoSendPaymentEmail: true,
      siaIndividualMember: this.siaIndividualMember,
      siaCorporateMember: this.siaCorporateMember,
      withdrawalProductTypes: this.withdrawalProductTypes,
      country: this.country ?? '',
    };
  }
}
